import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import {
  DocumentTextIcon,
  CpuChipIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  FolderIcon,
  CameraIcon,
  SparklesIcon,
  ChartPieIcon,
  Bars3Icon,
  XMarkIcon,
  CheckIcon,
  ChevronDownIcon,
  BoltIcon,
  ShieldCheckIcon,
  StarIcon,
} from '@heroicons/react/24/outline'
import SectionWrapper from '../components/landing/SectionWrapper'

const colors = {
  bgMain: '#080d12',
  bgSection: '#0d1318',
  bgCard: '#131c24',
  accent: '#1f9f93',
  accentLight: '#2fb6aa',
  textPrimary: 'rgba(255,255,255,.92)',
  textSecondary: 'rgba(255,255,255,.62)',
  textMuted: 'rgba(255,255,255,.40)',
  border: 'rgba(255,255,255,.08)',
  glow: 'rgba(31,159,147,.15)',
}

/* ───────── Navbar ───────── */
const Navbar = ({ t }) => {
  const [scrolled, setScrolled] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  const navLinks = [
    { label: t('nav.features'), href: '#features' },
    { label: t('nav.pricing'), href: '#pricing' },
    { label: t('nav.faq'), href: '#faq' },
  ]

  return (
    <nav
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
      style={{
        background: scrolled ? 'rgba(8,13,18,.85)' : 'transparent',
        backdropFilter: scrolled ? 'blur(16px)' : 'none',
        borderBottom: scrolled ? `1px solid ${colors.border}` : '1px solid transparent',
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo */}
          <div className="flex items-center gap-2.5">
            <img src="/logo.png" alt="Finomi" className="w-8 h-8 rounded-[8px] flex-shrink-0 object-contain" />
            <span className="text-lg font-extrabold tracking-wide" style={{ color: colors.textPrimary }}>
              Finomi
            </span>
          </div>

          {/* Desktop links */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="text-sm font-semibold no-underline transition-colors hover:text-white"
                style={{ color: colors.textSecondary }}
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-3">
            <Link
              to="/login"
              className="text-sm font-semibold no-underline px-4 py-2 rounded-lg transition-colors hover:text-white"
              style={{ color: colors.textSecondary }}
            >
              {t('nav.login')}
            </Link>
            <Link
              to="/register"
              className="text-sm font-bold no-underline px-5 py-2.5 rounded-lg transition-all"
              style={{
                background: `linear-gradient(135deg, ${colors.accent}, ${colors.accentLight})`,
                color: '#fff',
                boxShadow: `0 4px 14px rgba(31,159,147,.3)`,
              }}
            >
              {t('nav.getStarted')}
            </Link>
          </div>

          {/* Mobile hamburger */}
          <button
            className="md:hidden p-2 rounded-lg"
            style={{ color: colors.textSecondary }}
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <XMarkIcon className="w-6 h-6" /> : <Bars3Icon className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden overflow-hidden"
            style={{ background: 'rgba(8,13,18,.95)', backdropFilter: 'blur(16px)' }}
          >
            <div className="px-4 py-4 space-y-3">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={() => setMobileOpen(false)}
                  className="block text-sm font-semibold no-underline py-2"
                  style={{ color: colors.textSecondary }}
                >
                  {link.label}
                </a>
              ))}
              <div className="pt-3 space-y-2" style={{ borderTop: `1px solid ${colors.border}` }}>
                <Link
                  to="/login"
                  className="block text-sm font-semibold no-underline py-2"
                  style={{ color: colors.textSecondary }}
                >
                  {t('nav.login')}
                </Link>
                <Link
                  to="/register"
                  className="block text-sm font-bold no-underline px-4 py-2.5 rounded-lg text-center"
                  style={{
                    background: `linear-gradient(135deg, ${colors.accent}, ${colors.accentLight})`,
                    color: '#fff',
                  }}
                >
                  {t('nav.getStarted')}
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  )
}

/* ───────── Hero ───────── */
const Hero = ({ t }) => (
  <section
    className="relative min-h-screen flex items-center justify-center pt-20 pb-16 px-4 overflow-hidden"
    style={{ background: colors.bgMain }}
  >
    {/* Gradient mesh background */}
    <div
      className="absolute inset-0 pointer-events-none"
      style={{
        background: `
          radial-gradient(800px 600px at 30% 20%, rgba(31,159,147,.12), transparent),
          radial-gradient(600px 500px at 70% 80%, rgba(31,100,180,.06), transparent),
          radial-gradient(400px 400px at 50% 50%, rgba(31,159,147,.04), transparent)
        `,
      }}
    />
    <div className="relative max-w-4xl mx-auto text-center">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: 'easeOut' }}
      >
        <div
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold mb-8"
          style={{
            background: 'rgba(31,159,147,.1)',
            border: '1px solid rgba(31,159,147,.2)',
            color: colors.accentLight,
          }}
        >
          <SparklesIcon className="w-3.5 h-3.5" />
          {t('hero.badge')}
        </div>

        <h1
          className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black tracking-tight leading-[1.08]"
          style={{ color: colors.textPrimary }}
        >
          {t('hero.title')}
        </h1>

        <p
          className="mt-6 text-base sm:text-lg md:text-xl max-w-2xl mx-auto leading-relaxed"
          style={{ color: colors.textSecondary }}
        >
          {t('hero.subtitle')}
        </p>

        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link
            to="/register"
            className="px-8 py-3.5 rounded-xl text-base font-bold no-underline transition-all hover:scale-[1.02]"
            style={{
              background: `linear-gradient(135deg, ${colors.accent}, ${colors.accentLight})`,
              color: '#fff',
              boxShadow: `0 8px 30px rgba(31,159,147,.3)`,
            }}
          >
            {t('hero.cta')}
          </Link>
          <a
            href="#how-it-works"
            className="px-8 py-3.5 rounded-xl text-base font-semibold no-underline transition-all"
            style={{
              border: `1px solid ${colors.border}`,
              color: colors.textSecondary,
              background: 'rgba(255,255,255,.03)',
            }}
          >
            {t('hero.secondaryCta')}
          </a>
        </div>
      </motion.div>

      {/* App mockup glow */}
      <motion.div
        initial={{ opacity: 0, y: 60 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 0.3, ease: 'easeOut' }}
        className="mt-16 sm:mt-20 relative"
      >
        <div
          className="absolute -inset-4 rounded-2xl pointer-events-none"
          style={{
            background: `radial-gradient(50% 50% at 50% 50%, rgba(31,159,147,.12), transparent)`,
          }}
        />
        <div
          className="relative rounded-xl overflow-hidden"
          style={{
            border: `1px solid ${colors.border}`,
            background: colors.bgCard,
            boxShadow: '0 40px 80px rgba(0,0,0,.4)',
          }}
        >
          {/* Fake browser chrome */}
          <div className="flex items-center gap-2 px-4 py-3" style={{ borderBottom: `1px solid ${colors.border}` }}>
            <div className="w-3 h-3 rounded-full" style={{ background: 'rgba(255,255,255,.12)' }} />
            <div className="w-3 h-3 rounded-full" style={{ background: 'rgba(255,255,255,.12)' }} />
            <div className="w-3 h-3 rounded-full" style={{ background: 'rgba(255,255,255,.12)' }} />
            <div
              className="ml-4 flex-1 h-6 rounded-md max-w-xs"
              style={{ background: 'rgba(255,255,255,.05)' }}
            />
          </div>
          {/* Dashboard screenshot */}
          <img
            src="/screenshots/dashboard.png"
            alt="Finomi Dashboard"
            className="w-full block"
          />
        </div>
      </motion.div>
    </div>
  </section>
)

/* ───────── Social Proof ───────── */
const SocialProof = ({ t }) => {
  const stats = [
    { value: '50,000+', label: t('socialProof.receipts') },
    { value: '99.2%', label: t('socialProof.accuracy') },
    { value: '120h+', label: t('socialProof.saved') },
    { value: '2,000+', label: t('socialProof.users') },
  ]

  return (
    <SectionWrapper id="social-proof" className="py-16 px-4" style={{ background: colors.bgSection }}>
      <div className="max-w-5xl mx-auto">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((stat, i) => (
            <div key={i} className="text-center">
              <div className="text-3xl sm:text-4xl font-black" style={{ color: colors.accent }}>
                {stat.value}
              </div>
              <div className="mt-2 text-sm font-semibold" style={{ color: colors.textMuted }}>
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </SectionWrapper>
  )
}

/* ───────── Features ───────── */
const Features = ({ t }) => {
  const features = [
    { key: 'ocr', icon: CameraIcon },
    { key: 'expenses', icon: CurrencyDollarIcon },
    { key: 'budgets', icon: ChartPieIcon },
    { key: 'reports', icon: ChartBarIcon },
    { key: 'categories', icon: FolderIcon },
    { key: 'goals', icon: StarIcon },
  ]

  return (
    <SectionWrapper
      id="features"
      className="py-20 sm:py-28 px-4"
      style={{ background: colors.bgMain }}
    >
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-black" style={{ color: colors.textPrimary }}>
            {t('features.title')}
          </h2>
          <p className="mt-4 text-base sm:text-lg" style={{ color: colors.textSecondary }}>
            {t('features.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map(({ key, icon: Icon }, i) => (
            <motion.div
              key={key}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="group rounded-xl p-6 transition-all hover:-translate-y-1"
              style={{
                background: colors.bgCard,
                border: `1px solid ${colors.border}`,
              }}
            >
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center mb-4"
                style={{ background: 'rgba(31,159,147,.12)' }}
              >
                <Icon className="w-5 h-5" style={{ color: colors.accent }} />
              </div>
              <h3 className="text-base font-bold mb-2" style={{ color: colors.textPrimary }}>
                {t(`features.${key}.title`)}
              </h3>
              <p className="text-sm leading-relaxed" style={{ color: colors.textSecondary }}>
                {t(`features.${key}.description`)}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </SectionWrapper>
  )
}

/* ───────── How It Works ───────── */
const HowItWorks = ({ t }) => {
  const steps = [
    { key: 'step1', icon: CameraIcon, num: '01' },
    { key: 'step2', icon: SparklesIcon, num: '02' },
    { key: 'step3', icon: ChartPieIcon, num: '03' },
  ]

  return (
    <SectionWrapper
      id="how-it-works"
      className="py-20 sm:py-28 px-4"
      style={{ background: colors.bgSection }}
    >
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-black" style={{ color: colors.textPrimary }}>
            {t('howItWorks.title')}
          </h2>
          <p className="mt-4 text-base sm:text-lg" style={{ color: colors.textSecondary }}>
            {t('howItWorks.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-4 relative">
          {/* Connecting line (desktop) */}
          <div
            className="hidden md:block absolute top-14 left-[20%] right-[20%] h-px"
            style={{ background: `linear-gradient(to right, transparent, ${colors.accent}40, transparent)` }}
          />

          {steps.map(({ key, icon: Icon, num }, i) => (
            <motion.div
              key={key}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.5, delay: i * 0.15 }}
              className="text-center relative"
            >
              <div
                className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5 relative z-10"
                style={{
                  background: `linear-gradient(135deg, ${colors.accent}, ${colors.accentLight})`,
                  boxShadow: `0 12px 30px rgba(31,159,147,.25)`,
                }}
              >
                <Icon className="w-7 h-7 text-white" />
              </div>
              <span
                className="text-xs font-black tracking-widest"
                style={{ color: colors.accent }}
              >
                {num}
              </span>
              <h3 className="text-lg font-bold mt-2 mb-2" style={{ color: colors.textPrimary }}>
                {t(`howItWorks.${key}.title`)}
              </h3>
              <p className="text-sm leading-relaxed max-w-xs mx-auto" style={{ color: colors.textSecondary }}>
                {t(`howItWorks.${key}.description`)}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </SectionWrapper>
  )
}

/* ───────── Showcase ───────── */
const Showcase = ({ t }) => {
  const [activeTab, setActiveTab] = useState(0)
  const tabs = ['tab1', 'tab2', 'tab3']

  const screenshots = [
    '/screenshots/dashboard.png',
    '/screenshots/expenses.png',
    '/screenshots/documents.png',
  ]

  return (
    <SectionWrapper
      id="showcase"
      className="py-20 sm:py-28 px-4"
      style={{ background: colors.bgMain }}
    >
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-black" style={{ color: colors.textPrimary }}>
            {t('showcase.title')}
          </h2>
          <p className="mt-4 text-base sm:text-lg" style={{ color: colors.textSecondary }}>
            {t('showcase.subtitle')}
          </p>
        </div>

        {/* Tabs */}
        <div className="flex justify-center gap-2 mb-8">
          {tabs.map((tab, i) => (
            <button
              key={tab}
              onClick={() => setActiveTab(i)}
              className="px-5 py-2 rounded-lg text-sm font-bold transition-all"
              style={{
                background: activeTab === i ? 'rgba(31,159,147,.15)' : 'transparent',
                color: activeTab === i ? colors.accentLight : colors.textMuted,
                border: activeTab === i ? '1px solid rgba(31,159,147,.25)' : `1px solid transparent`,
              }}
            >
              {t(`showcase.${tab}`)}
            </button>
          ))}
        </div>

        {/* Content */}
        <div
          className="rounded-xl overflow-hidden"
          style={{
            border: `1px solid ${colors.border}`,
            background: colors.bgCard,
            boxShadow: '0 20px 60px rgba(0,0,0,.3)',
          }}
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
            >
              <img
                src={screenshots[activeTab]}
                alt={t(`showcase.${tabs[activeTab]}`)}
                className="w-full block"
              />
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </SectionWrapper>
  )
}

/* ───────── Smart Scan ───────── */
const SmartScan = ({ t }) => {
  const cards = [
    { key: 'accuracy', icon: ShieldCheckIcon, color: colors.accent },
    { key: 'speed', icon: BoltIcon, color: '#3b82f6' },
    { key: 'auto', icon: SparklesIcon, color: '#f59e0b' },
  ]

  return (
    <SectionWrapper
      id="smart-scan"
      className="py-20 sm:py-28 px-4"
      style={{ background: colors.bgSection }}
    >
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-black" style={{ color: colors.textPrimary }}>
            {t('smartScan.title')}
          </h2>
          <p className="mt-4 text-base sm:text-lg max-w-2xl mx-auto" style={{ color: colors.textSecondary }}>
            {t('smartScan.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {cards.map(({ key, icon: Icon, color }, i) => (
            <motion.div
              key={key}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="rounded-xl p-6"
              style={{ background: colors.bgCard, border: `1px solid ${colors.border}` }}
            >
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center mb-4"
                style={{ background: `${color}18` }}
              >
                <Icon className="w-5 h-5" style={{ color }} />
              </div>
              <h3 className="text-lg font-bold mb-2" style={{ color: colors.textPrimary }}>
                {t(`smartScan.${key}`)}
              </h3>
              <p className="text-sm leading-relaxed" style={{ color: colors.textSecondary }}>
                {t(`smartScan.${key}Desc`)}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </SectionWrapper>
  )
}

/* ───────── Pricing ───────── */
const Pricing = ({ t }) => {
  const plans = ['free', 'pro', 'business']

  return (
    <SectionWrapper
      id="pricing"
      className="py-20 sm:py-28 px-4"
      style={{ background: colors.bgMain }}
    >
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-black" style={{ color: colors.textPrimary }}>
            {t('pricing.title')}
          </h2>
          <p className="mt-4 text-base sm:text-lg" style={{ color: colors.textSecondary }}>
            {t('pricing.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {plans.map((plan, i) => {
            const isPro = plan === 'pro'
            const features = t(`pricing.${plan}.features`, { returnObjects: true })

            return (
              <motion.div
                key={plan}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="rounded-xl p-6 relative flex flex-col"
                style={{
                  background: colors.bgCard,
                  border: isPro ? `2px solid ${colors.accent}` : `1px solid ${colors.border}`,
                  boxShadow: isPro ? `0 0 40px rgba(31,159,147,.12)` : 'none',
                }}
              >
                {isPro && (
                  <div
                    className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-bold"
                    style={{ background: colors.accent, color: '#fff' }}
                  >
                    {t('pricing.popular')}
                  </div>
                )}

                <div className="mb-6">
                  <h3 className="text-lg font-bold" style={{ color: colors.textPrimary }}>
                    {t(`pricing.${plan}.name`)}
                  </h3>
                  <p className="text-sm mt-1" style={{ color: colors.textMuted }}>
                    {t(`pricing.${plan}.description`)}
                  </p>
                </div>

                <div className="mb-6">
                  {t(`pricing.${plan}.oldPrice`, { defaultValue: '' }) && (
                    <div className="mb-1">
                      <span className="text-sm font-bold line-through" style={{ color: '#e55353' }}>
                        {t(`pricing.${plan}.oldPrice`)}
                      </span>
                    </div>
                  )}
                  <span className="text-4xl font-black" style={{ color: colors.textPrimary }}>
                    {t(`pricing.${plan}.price`)}
                  </span>
                  {plan !== 'free' && (
                    <span className="text-sm font-semibold ml-1" style={{ color: colors.textMuted }}>
                      / {t('pricing.monthly')}
                    </span>
                  )}
                </div>

                <ul className="space-y-3 mb-8 flex-1">
                  {Array.isArray(features) && features.map((feature, fi) => (
                    <li key={fi} className="flex items-start gap-2.5">
                      <CheckIcon className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: colors.accent }} />
                      <span className="text-sm" style={{ color: colors.textSecondary }}>{feature}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  to="/register"
                  className="block text-center px-6 py-3 rounded-lg text-sm font-bold no-underline transition-all"
                  style={{
                    background: isPro
                      ? `linear-gradient(135deg, ${colors.accent}, ${colors.accentLight})`
                      : 'rgba(255,255,255,.06)',
                    color: isPro ? '#fff' : colors.textSecondary,
                    border: isPro ? 'none' : `1px solid ${colors.border}`,
                    boxShadow: isPro ? '0 4px 14px rgba(31,159,147,.25)' : 'none',
                  }}
                >
                  {t(`pricing.${plan}.cta`)}
                </Link>
              </motion.div>
            )
          })}
        </div>
      </div>
    </SectionWrapper>
  )
}

/* ───────── Testimonials ───────── */
const Testimonials = ({ t }) => {
  const items = t('testimonials.items', { returnObjects: true })

  return (
    <SectionWrapper
      id="testimonials"
      className="py-20 sm:py-28 px-4"
      style={{ background: colors.bgSection }}
    >
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-black" style={{ color: colors.textPrimary }}>
            {t('testimonials.title')}
          </h2>
          <p className="mt-4 text-base sm:text-lg" style={{ color: colors.textSecondary }}>
            {t('testimonials.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {Array.isArray(items) && items.map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="rounded-xl p-6"
              style={{ background: colors.bgCard, border: `1px solid ${colors.border}` }}
            >
              <p className="text-sm leading-relaxed mb-5 italic" style={{ color: colors.textSecondary }}>
                &ldquo;{item.quote}&rdquo;
              </p>
              <div>
                <div className="text-sm font-bold" style={{ color: colors.textPrimary }}>{item.author}</div>
                <div className="text-xs font-semibold mt-0.5" style={{ color: colors.textMuted }}>{item.role}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </SectionWrapper>
  )
}

/* ───────── FAQ ───────── */
const FAQ = ({ t }) => {
  const [openIndex, setOpenIndex] = useState(null)
  const items = t('faq.items', { returnObjects: true })

  return (
    <SectionWrapper
      id="faq"
      className="py-20 sm:py-28 px-4"
      style={{ background: colors.bgMain }}
    >
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-black" style={{ color: colors.textPrimary }}>
            {t('faq.title')}
          </h2>
          <p className="mt-4 text-base sm:text-lg" style={{ color: colors.textSecondary }}>
            {t('faq.subtitle')}
          </p>
        </div>

        <div className="space-y-3">
          {Array.isArray(items) && items.map((item, i) => (
            <div
              key={i}
              className="rounded-xl overflow-hidden"
              style={{ background: colors.bgCard, border: `1px solid ${colors.border}` }}
            >
              <button
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                className="w-full flex items-center justify-between p-5 text-left"
                style={{ color: colors.textPrimary }}
              >
                <span className="text-sm font-bold pr-4">{item.question}</span>
                <motion.div
                  animate={{ rotate: openIndex === i ? 180 : 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <ChevronDownIcon className="w-5 h-5 flex-shrink-0" style={{ color: colors.textMuted }} />
                </motion.div>
              </button>
              <AnimatePresence>
                {openIndex === i && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.25 }}
                    className="overflow-hidden"
                  >
                    <div className="px-5 pb-5">
                      <p className="text-sm leading-relaxed" style={{ color: colors.textSecondary }}>
                        {item.answer}
                      </p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </div>
    </SectionWrapper>
  )
}

/* ───────── Final CTA ───────── */
const FinalCTA = ({ t }) => (
  <SectionWrapper
    className="py-20 sm:py-28 px-4 relative overflow-hidden"
    style={{ background: colors.bgSection }}
  >
    <div
      className="absolute inset-0 pointer-events-none"
      style={{
        background: `
          radial-gradient(600px 400px at 50% 50%, rgba(31,159,147,.1), transparent)
        `,
      }}
    />
    <div className="relative max-w-2xl mx-auto text-center">
      <h2 className="text-3xl sm:text-4xl font-black" style={{ color: colors.textPrimary }}>
        {t('finalCta.title')}
      </h2>
      <p className="mt-4 text-base sm:text-lg" style={{ color: colors.textSecondary }}>
        {t('finalCta.subtitle')}
      </p>
      <div className="mt-8">
        <Link
          to="/register"
          className="inline-block px-8 py-3.5 rounded-xl text-base font-bold no-underline transition-all hover:scale-[1.02]"
          style={{
            background: `linear-gradient(135deg, ${colors.accent}, ${colors.accentLight})`,
            color: '#fff',
            boxShadow: `0 8px 30px rgba(31,159,147,.3)`,
          }}
        >
          {t('finalCta.cta')}
        </Link>
      </div>
    </div>
  </SectionWrapper>
)

/* ───────── Footer ───────── */
const Footer = ({ t }) => (
  <footer className="py-12 px-4" style={{ background: colors.bgMain, borderTop: `1px solid ${colors.border}` }}>
    <div className="max-w-6xl mx-auto">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
        {/* Brand */}
        <div className="col-span-2 md:col-span-1">
          <div className="flex items-center gap-2.5 mb-4">
            <img src="/logo.png" alt="Finomi" className="w-7 h-7 rounded-[6px] flex-shrink-0 object-contain" />
            <span className="text-base font-extrabold" style={{ color: colors.textPrimary }}>
              Finomi
            </span>
          </div>
          <p className="text-sm" style={{ color: colors.textMuted }}>
            {t('footer.description')}
          </p>
        </div>

        {/* Product */}
        <div>
          <h4 className="text-xs font-bold uppercase tracking-wider mb-4" style={{ color: colors.textMuted }}>
            {t('footer.product')}
          </h4>
          <ul className="space-y-2.5">
            {['features', 'pricing', 'faq'].map((item) => (
              <li key={item}>
                <a
                  href={`#${item}`}
                  className="text-sm no-underline transition-colors hover:text-white"
                  style={{ color: colors.textSecondary }}
                >
                  {t(`nav.${item}`)}
                </a>
              </li>
            ))}
          </ul>
        </div>

        {/* Company */}
        <div>
          <h4 className="text-xs font-bold uppercase tracking-wider mb-4" style={{ color: colors.textMuted }}>
            {t('footer.company')}
          </h4>
          <ul className="space-y-2.5">
            {['about', 'blog', 'careers', 'contact'].map((item) => (
              <li key={item}>
                <span className="text-sm" style={{ color: colors.textSecondary }}>
                  {t(`footer.${item}`)}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* Legal */}
        <div>
          <h4 className="text-xs font-bold uppercase tracking-wider mb-4" style={{ color: colors.textMuted }}>
            {t('footer.legal')}
          </h4>
          <ul className="space-y-2.5">
            {['privacy', 'terms'].map((item) => (
              <li key={item}>
                <span className="text-sm" style={{ color: colors.textSecondary }}>
                  {t(`footer.${item}`)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Copyright */}
      <div className="pt-8" style={{ borderTop: `1px solid ${colors.border}` }}>
        <p className="text-xs text-center" style={{ color: colors.textMuted }}>
          {t('footer.copyright')}
        </p>
      </div>
    </div>
  </footer>
)

/* ───────── Landing Page ───────── */
const LandingPage = () => {
  const { t } = useTranslation('landing')

  return (
    <div style={{ background: colors.bgMain }}>
      <Navbar t={t} />
      <Hero t={t} />
      <SocialProof t={t} />
      <Features t={t} />
      <HowItWorks t={t} />
      <Showcase t={t} />
      <SmartScan t={t} />
      <Pricing t={t} />
      <Testimonials t={t} />
      <FAQ t={t} />
      <FinalCTA t={t} />
      <Footer t={t} />
    </div>
  )
}

export default LandingPage
